import { Table, Group, Pagination, ActionIcon, Text, Box } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import React from 'react';
import styles from './ResourceTable.module.css'; // add your .clickableRow, etc.

export interface ResourceTableColumn<T> {
  /** unique key used as column id */
  key: string;
  /** header label */
  label: string | React.ReactNode;
  /** optional width or style */
  style?: React.CSSProperties;
  /** render function for cell content */
  render: (item: T) => React.ReactNode;
  /** set to false to hide column */
  visible?: boolean;
  /** disable click navigation for this cell (so icon click doesn’t trigger row click) */
  disableRowClick?: boolean;
}

export interface ResourceTableProps<T> {
  /** resource data array */
  data: T[];
  /** column definitions */
  columns: ResourceTableColumn<T>[];
  /** current page number */
  page: number;
  /** total number of pages */
  totalPages: number;
  /** total count of items (from server) */
  totalCount?: number;
  /** items per page (for calculating display range) */
  itemsPerPage?: number;
  /** triggered when user changes page */
  onPageChange: (page: number) => void;
  /** triggered when user clicks on a row */
  onRowClick?: (item: T) => void;
  /** optional custom action icon (defaults to eye icon) */
  actionIcon?: React.ReactNode;
  /** optional action handler */
  onActionClick?: (item: T) => void;
  /** text when no data */
  emptyText?: string | React.ReactNode;
  /** whether to show pagination */
  showPagination?: boolean;
  /** function to extract a unique key from each item (defaults to item.id or index) */
  getRowKey?: (item: T, index: number) => string | number;
}

export const ResourceTable = <T,>({
  data,
  columns,
  page,
  totalPages,
  onPageChange,
  onRowClick,
  onActionClick,
  actionIcon,
  emptyText = 'No records found',
  showPagination = true,
  getRowKey,
}: ResourceTableProps<T>) => {
  const visibleColumns = columns.filter((col) => col.visible !== false);

  if (!data || data.length === 0) {
    return (
      <Box p="xl" ta="center">
        <Text c="dimmed">{emptyText}</Text>
      </Box>
    );
  }

  return (
    <>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {visibleColumns.map((col) => (
              <Table.Th key={col.key} style={col.style}>
                {col.label}
              </Table.Th>
            ))}
            {onActionClick && <Table.Th style={{ width: 50 }} />}
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {data.map((item, idx) => {
            let key: string | number;
            if (getRowKey) {
              key = getRowKey(item, idx);
            } else {
              const id = (item as Record<string, unknown>)?.id;
              key = typeof id === 'string' || typeof id === 'number' ? id : idx;
            }
            return (
              <Table.Tr
                key={key}
                className={onRowClick ? styles.clickableRow : undefined}
                onClick={() => onRowClick?.(item)}
              >
                {visibleColumns.map((col) => (
                  <Table.Td
                    key={col.key}
                    onClick={(e) => {
                      if (col.disableRowClick) e.stopPropagation();
                    }}
                  >
                    {col.render(item)}
                  </Table.Td>
                ))}

                {onActionClick && (
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionClick(item);
                      }}
                    >
                      {actionIcon ?? <IconEye size={16} />}
                    </ActionIcon>
                  </Table.Td>
                )}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      {showPagination && totalPages > 1 && (
        <Group justify="center" p="md">
          <Pagination value={page} onChange={onPageChange} total={totalPages} />
        </Group>
      )}
    </>
  );
};
